/**
 * Prévision de trésorerie 13 semaines (Lot B) — section du Cockpit CFO.
 * Courbe du solde projeté + seuil de sécurité, scénarios, tableau hebdomadaire,
 * détail par semaine et réglages (hypothèses + charges récurrentes).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  AlertTriangle, CalendarClock, Loader2, Plus, Settings2, Trash2, TrendingDown, Wallet, X,
} from 'lucide-react';
import {
  Area, CartesianGrid, ComposedChart, Legend, Line, ReferenceLine,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { toast } from '../../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (v) => (v == null || isNaN(v) ? '—' : Math.round(v).toLocaleString('fr-FR'));
const fxof = (v) => `${fmt(v)} XOF`;
const short = (v) => {
  const a = Math.abs(v || 0);
  if (a >= 1e9) return `${(v / 1e9).toFixed(1)} Md`;
  if (a >= 1e6) return `${(v / 1e6).toFixed(1)} M`;
  if (a >= 1e3) return `${Math.round(v / 1e3)} k`;
  return String(v || 0);
};

const KIND_LABELS = {
  invoice_spot: 'Factures spot',
  invoice_contract: 'Factures contrat',
  to_invoice: 'À facturer',
  carrier_cost: 'Coûts transporteurs',
  payment_request: 'Demandes de paiement',
  recurring_charge: 'Charges récurrentes',
  vat: 'TVA',
};

const SCENARIOS = [
  { key: 'optimiste', label: 'Optimiste' },
  { key: 'nominal', label: 'Nominal' },
  { key: 'prudent', label: 'Prudent' },
];

const SettingsDialog = ({ open, onClose, onSaved }) => {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/finance/cash-forecast/settings`)
      .then(r => setForm({
        safety_balance: r.data.safety_balance,
        default_client_payment_days: r.data.default_client_payment_days,
        default_carrier_payment_days: r.data.default_carrier_payment_days,
        include_vat: r.data.include_vat,
        vat_declaration_day: r.data.vat_declaration_day,
        manual_start_balance: r.data.manual_start_balance ?? '',
        recurring_charges: r.data.recurring_charges || [],
      }))
      .catch(() => toast({ title: 'Chargement des réglages impossible', variant: 'destructive' }));
  }, [open]);

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setCharge = (i, k, v) => setForm(f => ({
    ...f, recurring_charges: f.recurring_charges.map((c, idx) => (idx === i ? { ...c, [k]: v } : c)),
  }));
  const addCharge = () => setForm(f => ({
    ...f,
    recurring_charges: [...f.recurring_charges, { label: '', amount: 0, periodicity: 'monthly', day: 1, end_date: null }],
  }));
  const removeCharge = (i) => setForm(f => ({
    ...f, recurring_charges: f.recurring_charges.filter((_, idx) => idx !== i),
  }));

  const save = async () => {
    const bad = (form.recurring_charges || []).find(c => !c.label?.trim() || !(Number(c.amount) > 0));
    if (bad) { toast({ title: 'Charge incomplète', description: 'Libellé et montant obligatoires.', variant: 'destructive' }); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/finance/cash-forecast/settings`, {
        safety_balance: Number(form.safety_balance) || 0,
        default_client_payment_days: Number(form.default_client_payment_days) || 0,
        default_carrier_payment_days: Number(form.default_carrier_payment_days) || 0,
        include_vat: !!form.include_vat,
        vat_declaration_day: Number(form.vat_declaration_day) || 15,
        manual_start_balance: form.manual_start_balance === '' ? null : Number(form.manual_start_balance),
        recurring_charges: (form.recurring_charges || []).map(c => ({
          id: c.id, label: c.label.trim(), amount: Number(c.amount),
          periodicity: c.periodicity, day: Number(c.day) || 1,
          end_date: c.end_date || null,
        })),
      });
      toast({ title: 'Hypothèses enregistrées' });
      onSaved?.();
      onClose();
    } catch (e) {
      toast({ title: 'Échec enregistrement', description: e?.response?.data?.detail, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="forecast-settings-dialog">
        <DialogHeader>
          <DialogTitle>Hypothèses de la prévision</DialogTitle>
          <DialogDescription className="text-xs">
            Ces paramètres pilotent le calcul des 13 semaines. Le délai réel observé par client
            prime toujours sur le délai par défaut.
          </DialogDescription>
        </DialogHeader>
        {!form ? (
          <div className="py-8 text-center text-zinc-500 text-sm"><Loader2 className="inline h-4 w-4 animate-spin mr-2" />Chargement…</div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Seuil de sécurité (XOF)</Label>
                <Input type="number" value={form.safety_balance} onChange={(e) => setField('safety_balance', e.target.value)}
                  className="tabular-nums" data-testid="forecast-safety-input" />
              </div>
              <div>
                <Label className="text-xs">Solde de départ manuel (vide = calculé)</Label>
                <Input type="number" value={form.manual_start_balance}
                  onChange={(e) => setField('manual_start_balance', e.target.value)}
                  placeholder="Calculé depuis les relevés" className="tabular-nums"
                  data-testid="forecast-manual-balance-input" />
              </div>
              <div>
                <Label className="text-xs">Délai client par défaut (jours)</Label>
                <Input type="number" value={form.default_client_payment_days}
                  onChange={(e) => setField('default_client_payment_days', e.target.value)}
                  className="tabular-nums" data-testid="forecast-client-days-input" />
              </div>
              <div>
                <Label className="text-xs">Délai transporteur (jours)</Label>
                <Input type="number" value={form.default_carrier_payment_days}
                  onChange={(e) => setField('default_carrier_payment_days', e.target.value)}
                  className="tabular-nums" data-testid="forecast-carrier-days-input" />
              </div>
              <div className="flex items-center gap-2 pt-5">
                <input type="checkbox" checked={!!form.include_vat} id="inc-vat"
                  onChange={(e) => setField('include_vat', e.target.checked)}
                  data-testid="forecast-include-vat" />
                <Label htmlFor="inc-vat" className="text-xs cursor-pointer">Inclure la TVA estimée</Label>
              </div>
              <div>
                <Label className="text-xs">Jour de déclaration TVA</Label>
                <Input type="number" min={1} max={28} value={form.vat_declaration_day}
                  onChange={(e) => setField('vat_declaration_day', e.target.value)}
                  className="tabular-nums" data-testid="forecast-vat-day-input" />
              </div>
            </div>

            <div className="border-t border-zinc-200 pt-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-bold uppercase tracking-wider text-zinc-500">
                  Charges récurrentes ({form.recurring_charges.length})
                </div>
                <Button size="sm" variant="outline" onClick={addCharge} className="h-7 text-xs"
                  data-testid="forecast-add-charge">
                  <Plus className="h-3 w-3 mr-1" /> Ajouter
                </Button>
              </div>
              {form.recurring_charges.length === 0 && (
                <div className="text-xs text-zinc-400 italic">
                  Aucune charge saisie — les décaissements de structure (loyers, salaires, assurances) manqueront à la prévision.
                </div>
              )}
              <div className="space-y-2">
                {form.recurring_charges.map((c, i) => (
                  <div key={c.id || i} className="grid grid-cols-12 gap-2 items-end" data-testid={`forecast-charge-row-${i}`}>
                    <div className="col-span-4">
                      <Label className="text-[10px]">Libellé</Label>
                      <Input value={c.label} onChange={(e) => setCharge(i, 'label', e.target.value)}
                        className="h-8 text-xs" data-testid={`forecast-charge-label-${i}`} />
                    </div>
                    <div className="col-span-3">
                      <Label className="text-[10px]">Montant</Label>
                      <Input type="number" value={c.amount} onChange={(e) => setCharge(i, 'amount', e.target.value)}
                        className="h-8 text-xs tabular-nums" data-testid={`forecast-charge-amount-${i}`} />
                    </div>
                    <div className="col-span-3">
                      <Label className="text-[10px]">Périodicité</Label>
                      <select value={c.periodicity} onChange={(e) => setCharge(i, 'periodicity', e.target.value)}
                        className="h-8 w-full text-xs border border-zinc-200 rounded-md px-1"
                        data-testid={`forecast-charge-periodicity-${i}`}>
                        <option value="weekly">Hebdomadaire</option>
                        <option value="monthly">Mensuelle</option>
                        <option value="quarterly">Trimestrielle</option>
                      </select>
                    </div>
                    <div className="col-span-1">
                      <Label className="text-[10px]">Jour</Label>
                      <Input type="number" min={1} max={28} value={c.day}
                        onChange={(e) => setCharge(i, 'day', e.target.value)}
                        className="h-8 text-xs tabular-nums px-1" data-testid={`forecast-charge-day-${i}`} />
                    </div>
                    <div className="col-span-1">
                      <button onClick={() => removeCharge(i)} className="p-1.5 rounded hover:bg-rose-50 text-rose-500"
                        data-testid={`forecast-charge-remove-${i}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Annuler</Button>
          <Button onClick={save} disabled={saving || !form} className="bg-[#16A34A] hover:bg-[#15803D] text-white"
            data-testid="forecast-settings-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const CashForecastTab = () => {
  const [scenario, setScenario] = useState('nominal');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/finance/cash-forecast`, { params: { weeks: 13, scenario } });
      setData(d);
    } catch (e) {
      toast({ title: 'Prévision indisponible', description: e?.response?.data?.detail, variant: 'destructive' });
      setData(null);
    } finally { setLoading(false); }
  }, [scenario]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (week) => {
    setDetailLoading(true);
    setDetail({ week, inflows: [], outflows: [] });
    try {
      const { data: d } = await axios.get(`${API}/finance/cash-forecast/detail`, { params: { week, scenario } });
      setDetail(d);
    } catch (e) {
      toast({ title: 'Détail indisponible', description: e?.response?.data?.detail, variant: 'destructive' });
      setDetail(null);
    } finally { setDetailLoading(false); }
  };

  const chartData = useMemo(() => (data?.weeks_detail || []).map(w => ({
    name: w.label.replace('Sem. du ', ''),
    solde: w.projected_balance,
    encaissements: w.inflows,
    decaissements: -w.outflows,
    week: w.week,
  })), [data]);

  if (loading) {
    return (
      <div className="py-16 text-center text-zinc-500 text-sm" data-testid="forecast-loading">
        <Loader2 className="inline h-4 w-4 animate-spin mr-2" /> Calcul de la prévision…
      </div>
    );
  }
  if (!data) {
    return <div className="py-12 text-center text-zinc-400 text-sm" data-testid="forecast-empty">Aucune donnée de prévision.</div>;
  }

  const safety = data.settings.safety_balance;

  return (
    <div className="space-y-4" data-testid="tab-cash-forecast">
      {/* Barre scénarios + réglages */}
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="flex items-center gap-1 bg-zinc-100 rounded-lg p-1" data-testid="forecast-scenarios">
          {SCENARIOS.map(s => (
            <button key={s.key} onClick={() => setScenario(s.key)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                scenario === s.key ? 'bg-white shadow text-zinc-900' : 'text-zinc-500 hover:text-zinc-800'
              }`}
              data-testid={`forecast-scenario-${s.key}`}>
              {s.label}
            </button>
          ))}
        </div>
        <Button size="sm" variant="outline" onClick={() => setSettingsOpen(true)} className="h-8 text-xs"
          data-testid="forecast-settings-btn">
          <Settings2 className="h-3.5 w-3.5 mr-1.5" /> Hypothèses
        </Button>
      </div>
      <div className="text-[11px] text-zinc-500 italic" data-testid="forecast-assumption">{data.assumptions}</div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="bg-white border border-zinc-200 rounded-xl p-3" data-testid="forecast-kpi-start">
          <div className="text-[10px] uppercase font-bold tracking-wider text-zinc-500 flex items-center gap-1">
            <Wallet className="h-3 w-3" /> Solde de départ
          </div>
          <div className="text-lg font-bold tabular-nums mt-1">{fxof(data.start_balance.amount)}</div>
          <div className="text-[10px] text-zinc-500 mt-0.5">
            {data.start_balance.manual_override ? 'Saisi manuellement' : 'Relevés au '}
            {!data.start_balance.manual_override && data.start_balance.as_of
              ? new Date(data.start_balance.as_of).toLocaleDateString('fr-FR') : ''}
          </div>
        </div>
        <div className="bg-white border border-zinc-200 rounded-xl p-3" data-testid="forecast-kpi-end">
          <div className="text-[10px] uppercase font-bold tracking-wider text-zinc-500">Solde projeté à 13 sem.</div>
          <div className={`text-lg font-bold tabular-nums mt-1 ${data.totals.end_balance < safety ? 'text-rose-700' : 'text-emerald-700'}`}>
            {fxof(data.totals.end_balance)}
          </div>
          <div className="text-[10px] text-zinc-500 mt-0.5">
            Encaissements {short(data.totals.inflows)} · Décaissements {short(data.totals.outflows)}
          </div>
        </div>
        <div className={`rounded-xl p-3 border ${data.tension_week ? 'bg-rose-50 border-rose-200' : 'bg-emerald-50 border-emerald-200'}`}
          data-testid="forecast-kpi-tension">
          <div className={`text-[10px] uppercase font-bold tracking-wider flex items-center gap-1 ${data.tension_week ? 'text-rose-700' : 'text-emerald-700'}`}>
            <AlertTriangle className="h-3 w-3" /> Semaine de tension
          </div>
          {data.tension_week ? (
            <>
              <div className="text-sm font-bold text-rose-800 mt-1">
                Tension {data.tension_week.label.toLowerCase()} : {fxof(data.tension_week.projected_balance)}
              </div>
              <div className="text-[10px] text-rose-700 mt-0.5">
                {fxof(Math.abs(data.tension_week.gap))} sous le seuil de sécurité
              </div>
            </>
          ) : (
            <>
              <div className="text-sm font-bold text-emerald-800 mt-1">Aucune tension détectée</div>
              <div className="text-[10px] text-emerald-700 mt-0.5">Seuil de sécurité : {fxof(safety)}</div>
            </>
          )}
        </div>
        <div className="bg-white border border-zinc-200 rounded-xl p-3" data-testid="forecast-kpi-need">
          <div className="text-[10px] uppercase font-bold tracking-wider text-zinc-500 flex items-center gap-1">
            <TrendingDown className="h-3 w-3" /> Besoin de financement max
          </div>
          <div className="text-lg font-bold tabular-nums mt-1">{fxof(data.max_financing_need)}</div>
          <div className="text-[10px] text-zinc-500 mt-0.5">
            Point bas : {data.lowest_point ? `${data.lowest_point.label.toLowerCase()} · ${short(data.lowest_point.projected_balance)}` : '—'}
          </div>
        </div>
      </div>

      {/* Courbe */}
      <div className="bg-white border border-zinc-200 rounded-xl p-3" data-testid="forecast-chart">
        <div className="text-xs font-semibold text-zinc-700 mb-2">Solde projeté sur 13 semaines</div>
        <div style={{ width: '100%', height: 280 }}>
          <ResponsiveContainer>
            <ComposedChart data={chartData} margin={{ top: 5, right: 10, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-25} textAnchor="end" height={50} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={short} />
              <Tooltip formatter={(v, n) => [fxof(Math.abs(v)), n]} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <ReferenceLine y={safety} stroke="#dc2626" strokeDasharray="4 4"
                label={{ value: 'Seuil de sécurité', position: 'insideTopRight', fontSize: 10, fill: '#dc2626' }} />
              <Area type="monotone" dataKey="encaissements" fill="#d1fae5" stroke="#10b981" name="Encaissements" />
              <Area type="monotone" dataKey="decaissements" fill="#fee2e2" stroke="#ef4444" name="Décaissements" />
              <Line type="monotone" dataKey="solde" stroke="#2563eb" strokeWidth={2} dot={{ r: 2 }} name="Solde projeté" />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Tableau 13 semaines */}
      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="forecast-table">
            <thead className="bg-zinc-50 text-zinc-600 uppercase text-[10px] tracking-wider">
              <tr>
                <th className="px-3 py-2 text-left">Semaine</th>
                <th className="px-3 py-2 text-right">Encaissements</th>
                <th className="px-3 py-2 text-right">Décaissements</th>
                <th className="px-3 py-2 text-right">Flux net</th>
                <th className="px-3 py-2 text-right">Solde projeté</th>
                <th className="px-3 py-2 text-center">Lignes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {data.weeks_detail.map(w => (
                <tr key={w.week}
                  onClick={() => openDetail(w.week)}
                  className={`cursor-pointer hover:bg-blue-50/40 ${w.below_safety ? 'bg-rose-50/60' : ''}`}
                  data-testid={`forecast-week-${w.week}`}>
                  <td className="px-3 py-2 font-medium text-zinc-800">
                    {w.label}
                    {w.below_safety && (
                      <Badge className="ml-2 text-[9px] bg-rose-100 text-rose-700 border-rose-200">sous le seuil</Badge>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-emerald-700">{w.inflows ? fmt(w.inflows) : '—'}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-rose-700">{w.outflows ? fmt(w.outflows) : '—'}</td>
                  <td className={`px-3 py-2 text-right tabular-nums font-semibold ${w.net >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                    {w.net >= 0 ? '+' : ''}{fmt(w.net)}
                  </td>
                  <td className={`px-3 py-2 text-right tabular-nums font-bold ${w.below_safety ? 'text-rose-700' : 'text-zinc-900'}`}>
                    {fmt(w.projected_balance)}
                  </td>
                  <td className="px-3 py-2 text-center text-zinc-500">{w.items_count || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Top encaissements */}
      {data.top_inflows?.length > 0 && (
        <div className="bg-white border border-zinc-200 rounded-xl p-3" data-testid="forecast-top-inflows">
          <div className="text-xs font-semibold text-zinc-700 mb-2">
            Top 5 des encaissements à sécuriser
          </div>
          <div className="space-y-1.5">
            {data.top_inflows.map((t, i) => (
              <div key={i} className="flex items-center justify-between text-xs border-b border-zinc-50 pb-1.5 last:border-0">
                <div className="min-w-0">
                  <div className="font-medium text-zinc-800 truncate">{t.label}</div>
                  <div className="text-[10px] text-zinc-500">
                    {t.counterparty || '—'} · {KIND_LABELS[t.kind] || t.kind} · attendu le {new Date(t.date).toLocaleDateString('fr-FR')}
                  </div>
                </div>
                <div className="font-bold tabular-nums text-emerald-700 shrink-0 ml-3">{fxof(t.amount)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Détail d'une semaine */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="forecast-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarClock className="h-4 w-4 text-blue-600" /> Détail — {detail?.week}
            </DialogTitle>
            <DialogDescription className="text-xs">
              Lignes retenues dans le scénario {scenario}.
            </DialogDescription>
          </DialogHeader>
          {detailLoading ? (
            <div className="py-8 text-center text-zinc-500 text-sm"><Loader2 className="inline h-4 w-4 animate-spin mr-2" />Chargement…</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[['Encaissements', detail?.inflows || [], 'emerald'], ['Décaissements', detail?.outflows || [], 'rose']].map(([title, rows, color]) => (
                <div key={title}>
                  <div className={`text-[11px] font-bold uppercase tracking-wider text-${color}-700 mb-1`}>
                    {title} ({rows.length})
                  </div>
                  {rows.length === 0 && <div className="text-xs text-zinc-400 italic">Aucune ligne.</div>}
                  <div className="space-y-1">
                    {rows.map((r, i) => (
                      <div key={i} className="flex items-start justify-between text-xs border border-zinc-100 rounded-md px-2 py-1.5"
                        data-testid={`forecast-detail-${title === 'Encaissements' ? 'in' : 'out'}-${i}`}>
                        <div className="min-w-0">
                          <div className="font-medium text-zinc-800 truncate">{r.label}</div>
                          <div className="text-[10px] text-zinc-500 truncate">
                            {r.counterparty || '—'} · {KIND_LABELS[r.kind] || r.kind} · {new Date(r.date).toLocaleDateString('fr-FR')}
                            {r.age_days > 0 ? ` · échue depuis ${r.age_days} j` : ''}
                          </div>
                        </div>
                        <div className={`font-bold tabular-nums text-${color}-700 shrink-0 ml-2`}>{fmt(r.amount)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetail(null)} data-testid="forecast-detail-close">
              <X className="h-3.5 w-3.5 mr-1" /> Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} onSaved={load} />
    </div>
  );
};

export default CashForecastTab;
